import { useEffect } from "react";
import DataTable, { Column } from "../../components/DataTable";
import { AppDispatch, RootState } from "../../store";
import { useDispatch } from "react-redux";
import { useSelector } from "react-redux";
import { fetchClasses, setFilters } from "../../features/classes/ClassesSlice";
import { useNavigate } from "react-router-dom";
import { ClassApiResponse } from "../../types/classesTypes";
import PaginationBar from "../../components/PaginationBar";
import DownloadButton from "../../components/common/DownloadButton";
import { exportToExcel } from "../../util/functions/UniversalFunctions";
import Tag from "../../components/Tags";
import { useBranchCode } from "../../util/hooks/useBranch";

export default function ClassesList() {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();

  const branchCode = useBranchCode();

  const { classes, isLoading, filters, page, size, total } = useSelector(
    (state: RootState) => state.classes
  );

  useEffect(() => {
    dispatch(fetchClasses({ branchCode, page, size, filters }));
  }, [dispatch, branchCode, page, size, filters]);

  const columns: Column<ClassApiResponse>[] = [
    {
      label: "SL NO.",
      accessor: "classId",
      render: (item,i) => (
        <div>
          {i + 1}
        </div>
      ),
    },
    {
      label: "Class Name",
      accessor: "className",
      render: (item) => (
        <div className="text-neutral-400 text-sm font-bold">
          {item.className}
        </div>
      ),
    },
    {
      label: "Academic Cycle",
      accessor: "academicCycle",
      // searchable: true,
      render: (item) => <Tag text={item.academicCycle} />,
    },
    {
      label: "No. of Students",
      accessor: "noOfStudents",
      // searchable: true,
      render: (item) => (
        <p className="w-full text-[13px]">{item.noOfStudents ?? "---"}</p>
      ),
    },
    {
      label: "Action",
      accessor: "action",
      render: (item) => (
        <div className="flex gap-3 w-full">
          <Tag
            onClick={() =>
              navigate(`/classes/classes-details/${item.classId}?tab=Students`)
            }
            text="View Details"
            color="warning"
            size="sm"
          />
          <Tag
            onClick={() =>
              navigate(`/classes/attendance-details/${item.classId}`, {
                state: {
                  className: item.className,
                  academicCycle: item.academicCycle,
                },
              })
            }
            text="Attendance"
            color="success"
            size="sm"
          />
        </div>
      ),
    },
  ];

  const handleSearchChange = (accessor: string, value: string) => {
    const updatedFilters = { ...filters, [accessor]: value };
    dispatch(setFilters(updatedFilters));
  };

  const handlePageChange = (newPage: number) => {
    dispatch(fetchClasses({ branchCode, page: newPage, size, filters }));
  };

  const handlePageSizeChange = (newSize: number) => {
    dispatch(fetchClasses({ branchCode, page: 0, size: newSize, filters }));
  };

  const handleDownload = () => {
    if (classes && classes?.length > 0) {
      exportToExcel(classes ?? [], "classList");
    }
  };

  return (
    <div className="flex flex-col gap-2 ">
      <div className="flex gap-3 items-center justify-end">
        <PaginationBar
          currentPage={page}
          pageSize={size}
          totalItems={total}
          onPageChange={handlePageChange}
          onPageSizeChange={handlePageSizeChange}
        />

        <DownloadButton
          onClick={() => handleDownload()}
          disabled={isLoading || classes.length === 0}
        />
      </div>
      <div className="grid gap-7 bg-white px-6 py-5 rounded-lg">
        <DataTable
          columns={columns}
          data={classes}
          searchFilters={filters}
          onSearchChange={handleSearchChange}
          isLoading={isLoading}
        />
      </div>
    </div>
  );
}
